import { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  Timestamp,
} from "firebase/firestore";
import { toast } from "react-hot-toast";
import { X } from "lucide-react";
import { countryToDial, toE164, digitsOnly } from "../../utils/phone";

const CURRENCIES = ["LKR", "USD", "EUR", "GBP", "INR"];

function symbolOf(code) {
  switch (code) {
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    case "INR":
      return "₹";
    case "LKR":
      return "Rs";
    default:
      return code || "$";
  }
}

function cleanUndefined(obj) {
  const o = {};
  Object.keys(obj).forEach((k) => {
    if (obj[k] !== undefined) o[k] = obj[k];
  });
  return o;
}

// find a user by E.164 or legacy local digits
async function findUserByPhone(dbRef, { e164, digits }) {
  const tries = [
    ["phoneE164", e164],
    ["phone", digits],
    ["mobile", digits],
    ["mobile", e164],
  ].filter(([, v]) => !!v);

  for (const [field, val] of tries) {
    const qy = query(collection(dbRef, "users"), where(field, "==", val));
    const snap = await getDocs(qy);
    if (!snap.empty) {
      const d = snap.docs[0];
      return { id: d.id, ...d.data() };
    }
  }
  return null;
}

function parseLuckyList(txt = "") {
  if (!txt) return [];
  return [
    ...new Set(
      txt
        .split(/[,\s]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => Number(s))
        .filter((n) => Number.isInteger(n) && n >= 0 && n <= 99)
    ),
  ].sort((a, b) => a - b);
}

function defaultDrawTime(which = "today") {
  // 9:30 PM local
  const d = new Date();
  if (which === "tomorrow") d.setDate(d.getDate() + 1);
  d.setHours(21, 30, 0, 0);
  return d;
}

export default function CreateCardModal({
  open,
  onClose,
  agentUid,
  agentStatus = "approved",
  onCreated, // (ids: string[]) => void
}) {
  const [howMany, setHowMany] = useState(1);
  const [drawDay, setDrawDay] = useState("today"); // today | tomorrow
  const [pricePerNumber, setPricePerNumber] = useState(100);
  const [currency, setCurrency] = useState("LKR");
  const [payoutPct, setPayoutPct] = useState(80);

  // VIP rows: { mobile: string, lucky: string, applyCards: number }
  const [vips, setVips] = useState([{ mobile: "", lucky: "", applyCards: 1 }]);
  const [busy, setBusy] = useState(false);

  // Agent document (for defaults such as dial)
  const [agentDoc, setAgentDoc] = useState(null);
  useEffect(() => {
    if (!open || !agentUid) return;
    const unsub = onSnapshot(doc(db, "agents", agentUid), (d) =>
      setAgentDoc(d.exists() ? { id: d.id, ...d.data() } : null)
    );
    return () => unsub?.();
  }, [open, agentUid]);

  const defaultDial = useMemo(
    () => countryToDial(agentDoc?.countryCode || agentDoc?.country) || "+94",
    [agentDoc?.countryCode, agentDoc?.country]
  );

  function addVipRow() {
    setVips((prev) => [
      ...prev,
      { mobile: "", lucky: "", applyCards: Math.max(1, Math.min(howMany, 1)) },
    ]);
  }
  function removeVipRow(i) {
    setVips((prev) => prev.filter((_, idx) => idx !== i));
  }
  function updateVip(i, patch) {
    setVips((prev) => {
      const copy = [...prev];
      copy[i] = { ...copy[i], ...patch };
      return copy;
    });
  }

  // keep applyCards within [1 .. howMany] if howMany changes
  useEffect(() => {
    setVips((prev) =>
      prev.map((v) => ({
        ...v,
        applyCards: Math.max(
          1,
          Math.min(Number(v.applyCards || 1), Number(howMany || 1))
        ),
      }))
    );
  }, [howMany]);

  async function handleCreate() {
    if (agentStatus !== "approved") {
      toast.error("Your agent status is not approved.");
      return;
    }
    const nCards = Number(howMany || 0);
    if (!Number.isInteger(nCards) || nCards < 1) {
      toast.error("Enter a valid card count.");
      return;
    }
    const unit = Number(pricePerNumber || 0);
    if (!(unit > 0)) {
      toast.error("Enter a valid price per number.");
      return;
    }

    // validate VIP rows and clamp applyCards ≤ nCards
    const vipRows = vips
      .map((r) => ({
        raw: r,
        mobileTrim: (r.mobile || "").trim(),
        luckyList: parseLuckyList(r.lucky || ""),
        applyCards: Math.max(1, Math.min(Number(r.applyCards || 1), nCards)),
      }))
      .filter((r) => r.mobileTrim); // ignore empty mobile rows

    setBusy(true);
    try {
      const agentName =
        agentDoc?.fullName ||
        agentDoc?.username ||
        agentDoc?.name ||
        agentDoc?.email ||
        (agentUid ? agentUid.slice(0, 6) : "Agent");

      // 1) Create N card documents
      const drawAtJS = defaultDrawTime(drawDay); // JS Date
      const drawAt = Timestamp.fromDate(drawAtJS);
      const createdCardIds = [];
      for (let i = 0; i < nCards; i++) {
        const ref = await addDoc(
          collection(db, "cards"),
          cleanUndefined({
            createdAt: serverTimestamp(),
            agentUid,
            agentName,
            status: "active",
            currency,
            pricePerNumber: unit,
            payoutPct: Number(payoutPct || 80),
            drawAt,
            filled: [],
            locked: [],
            todaySales: 0,
          })
        );
        createdCardIds.push(ref.id);
      }

      // 2) VIP allocations
      // For each VIP row, apply to *up to* applyCards distinct newly-created cards.
      // If a card yields zero numbers (all conflicts), it does NOT count toward applyCards.
      for (const row of vipRows) {
        const e164 = toE164(row.mobileTrim, defaultDial);
        const digits = digitsOnly(row.mobileTrim);
        const lucky = row.luckyList;
        if (!lucky.length) continue; // nothing to allocate

        // try to find a matching user once per VIP row
        const foundUser = await findUserByPhone(db, { e164, digits });
        let remaining = row.applyCards;

        for (const cardId of createdCardIds) {
          if (remaining <= 0) break;

          // one transaction per card attempt
          const allocatedOnThisCard = await runTransaction(db, async (tx) => {
            const cardRef = doc(db, "cards", cardId);
            const cardSnap = await tx.get(cardRef);
            if (!cardSnap.exists()) throw new Error("Card disappeared.");

            const c = cardSnap.data();
            const filled = new Set(c.filled || []);
            const locked = new Set(c.locked || []);

            const availableLucky = lucky.filter(
              (n) => !filled.has(n) && !locked.has(n)
            );
            if (!availableLucky.length) {
              return false; // nothing to allocate here; do not count this card
            }

            const tRef = doc(collection(db, "tickets"));
            const safeAgentName =
              c.agentName ||
              agentName ||
              (agentUid ? agentUid.slice(0, 6) : "Agent");
            const bundleId = `${agentUid}|${foundUser?.id || e164}`;
            const amount = Number((availableLucky.length * unit).toFixed(2));

            tx.set(
              tRef,
              cleanUndefined({
                id: tRef.id,
                status: "payment_approved", // direct-sold
                allocated: true,
                allocatedBy: agentUid,
                allocatedAt: serverTimestamp(),
                cardId,

                agentUid,
                agentName: safeAgentName,

                userUid: foundUser?.id || null,
                userName:
                  foundUser?.username ||
                  foundUser?.email ||
                  (foundUser?.id
                    ? foundUser.id.slice(0, 6)
                    : `Mobile ${String(digits).slice(-4)}`),

                userMobileE164: e164,
                userMobile: e164,

                numbers: availableLucky,
                currency,
                unitPrice: unit,
                amount,
                paymentApprovedAt: serverTimestamp(),
                bundleId,
              })
            );

            tx.update(cardRef, {
              filled: arrayUnion(...availableLucky),
              locked: arrayRemove(...availableLucky),
              todaySales: (c.todaySales || 0) + amount,
            });

            return true; // we successfully allocated at least 1 number here
          });

          if (allocatedOnThisCard) {
            remaining -= 1; // count only cards where at least one number was placed
          }
        }

        // If remaining > 0 here, we simply couldn't fit them on enough cards
        // (because of conflicts with earlier VIP rows). That's OK by design.
      }

      toast.success(
        `Created ${createdCardIds.length} card(s) with VIP allocations applied.`
      );
      onClose?.();
      onCreated?.(createdCardIds);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to create cards");
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60"
        onClick={busy ? undefined : onClose}
      />
      <div className="relative w-full md:max-w-2xl bg-slate-900 border border-slate-700 rounded-2xl p-4 m-2">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-bold">Create Cards</h2>
          <button
            onClick={onClose}
            className="px-2 py-1 rounded bg-slate-700 text-sm inline-flex items-center gap-1"
            disabled={busy}
          >
            <X size={16} /> Close
          </button>
        </div>

        {/* row 1: draw day + how many */}
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <div className="text-sm text-slate-300 mb-1">Draw Day</div>
            <div className="flex gap-2">
              <button
                className={`px-3 py-1.5 rounded-xl ${
                  drawDay === "today"
                    ? "bg-lime-400 text-black font-semibold"
                    : "bg-slate-700 text-white"
                }`}
                onClick={() => setDrawDay("today")}
              >
                Today (9:30 PM)
              </button>
              <button
                className={`px-3 py-1.5 rounded-xl ${
                  drawDay === "tomorrow"
                    ? "bg-lime-400 text-black font-semibold"
                    : "bg-slate-700 text-white"
                }`}
                onClick={() => setDrawDay("tomorrow")}
              >
                Tomorrow (9:30 PM)
              </button>
            </div>
            <div className="text-xs text-slate-400 mt-1">
              Auto-sets draw time to 9:30 PM.
            </div>
          </div>

          <div>
            <label className="text-sm text-slate-300 mb-1 block">
              How many cards?
            </label>
            <input
              type="number"
              min={1}
              value={howMany}
              onChange={(e) => {
                const v = Math.max(1, Number(e.target.value || 1));
                setHowMany(v);
              }}
              className="w-full px-3 py-2 rounded bg-slate-800 text-white"
            />
          </div>
        </div>

        {/* row 2: price + currency + payout */}
        <div className="grid sm:grid-cols-3 gap-3 mt-3">
          <div>
            <label className="text-sm text-slate-300 mb-1 block">
              Price per number
            </label>
            <input
              type="number"
              min={0}
              value={pricePerNumber}
              onChange={(e) => setPricePerNumber(Number(e.target.value || 0))}
              className="w-full px-3 py-2 rounded bg-slate-800 text-white"
            />
          </div>
          <div>
            <label className="text-sm text-slate-300 mb-1 block">
              Currency
            </label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full px-3 py-2 rounded bg-slate-800 text-white"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm text-slate-300 mb-1 block">
              Payout %
            </label>
            <input
              type="number"
              min={1}
              max={100}
              value={payoutPct}
              onChange={(e) => setPayoutPct(Number(e.target.value || 80))}
              className="w-full px-3 py-2 rounded bg-slate-800 text-white"
            />
          </div>
        </div>

        {/* VIP block */}
        <div className="mt-4 p-4 rounded-2xl bg-slate-800 border border-slate-700">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold">VIP Pre-Allocation</div>
            <button
              type="button"
              onClick={addVipRow}
              className="px-2 py-1 rounded bg-slate-700 text-sm"
              disabled={busy}
            >
              + Add VIP
            </button>
          </div>
          <div className="text-xs text-slate-400 mb-3">
            For each VIP: enter <b>mobile</b> and <b>lucky numbers</b>{" "}
            (comma-separated). Each VIP’s lucky numbers will be allocated{" "}
            <b>once per card</b> on the first <b>“Cards to apply”</b> newly
            created cards (max equals the number of cards you’re creating).
          </div>

          <div className="space-y-2">
            {vips.map((r, i) => (
              <div
                key={i}
                className="grid sm:grid-cols-[1fr_1fr_140px_auto] gap-2 items-center"
              >
                <input
                  placeholder="Lucky numbers (e.g. 24, 36, 89)"
                  value={r.lucky}
                  onChange={(e) => updateVip(i, { lucky: e.target.value })}
                  className="px-3 py-2 rounded bg-slate-900 text-white ring-1 ring-slate-700"
                />
                <input
                  placeholder="Mobile (e.g. +94761234567 or 0761234567)"
                  value={r.mobile}
                  onChange={(e) => updateVip(i, { mobile: e.target.value })}
                  className="px-3 py-2 rounded bg-slate-900 text-white ring-1 ring-slate-700"
                />
                <input
                  type="number"
                  min={1}
                  max={Number(howMany || 1)}
                  value={r.applyCards}
                  onChange={(e) =>
                    updateVip(i, {
                      applyCards: Math.max(
                        1,
                        Math.min(
                          Number(howMany || 1),
                          Number(e.target.value || 1)
                        )
                      ),
                    })
                  }
                  className="px-3 py-2 rounded bg-slate-900 text-white ring-1 ring-slate-700"
                  title="Cards to apply (how many of the newly created cards will get these numbers)"
                />
                <button
                  onClick={() => removeVipRow(i)}
                  className="px-3 py-2 rounded bg-slate-700 text-white"
                  disabled={busy || vips.length === 1}
                  title="Remove row"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* footer */}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-700 text-white"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={busy}
            className="px-4 py-2 rounded-xl bg-lime-400 text-black font-semibold"
          >
            {busy ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
